"use client";

import { Brain, Heart, Moon, Wind } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhichStandardsApplyToArcFlashLabels() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    icon: Heart,
    title: "NFPA 70E Compliance",
    description: "Our labeling solutions strictly adhere to NFPA 70E requirements, ensuring that all arc flash warnings provide the necessary safety information for electrical personnel.",
    citation: "National Fire Protection Association, 2024"
  }, {
    icon: Moon,
    title: "ANSI Z535 Standards",
    description: "We utilize ANSI Z535 design principles for all signage, incorporating standardized signal words, colors, and symbols to maximize hazard communication effectiveness.",
    citation: "American National Standards Institute, 2023"
  }, {
    icon: Wind,
    title: "OSHA Safety Regulations",
    description: "Labels are engineered to meet OSHA 1910.145 specifications, providing durable, high-visibility warnings that protect workers from potential electrical arc flash incidents.",
    citation: "Occupational Safety and Health Administration, 2024"
  }, {
    icon: Brain,
    title: "IEEE 1584 Calculations",
    description: "We integrate precise data derived from IEEE 1584 arc flash hazard analysis, ensuring that every label reflects accurate incident energy and boundary information.",
    citation: "Institute of Electrical and Electronics Engineers, 2022"
  }];
  return <section data-section-id="991" id="which-standards-apply-to-arc-flash-labels" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-lg opacity-30 blur-3xl" style={{
      background: "radial-gradient(circle at 30% 30%, hsl(45 68% 47% / 0.3), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-6xl">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
            Regulatory Compliance
          </p>
          <h2 className="font-serif font-default mt-6 text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05] tracking-tight text-foreground">
            Standards for Arc Flash Safety.
          </h2>
          <p className="mt-8 mx-auto max-w-[600px] text-base md:text-lg leading-relaxed text-muted-foreground font-default">
            Navigating electrical safety requires strict adherence to industry protocols. 
            Our labels are manufactured to meet the rigorous demands of national safety 
            standards, ensuring your facility remains compliant and your team protected.
          </p>
        </div>

        {}
        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <FeatureCard key={i} data-index={i} index={i} Icon={Icon} title={feature.title} description={feature.description} citation={feature.citation} />;
        })}
        </div>
      </div>
    </section>;
}
function FeatureCard({
  index,
  Icon,
  title,
  description,
  citation,
  ...rest
}: {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  citation: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="group h-full bg-card text-card-foreground rounded-lg border border-border p-8 md:p-12 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-500 ease-out">
        <div className="flex flex-col gap-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
          </div>

          <h3 className="font-serif font-default text-2xl md:text-[28px] font-light leading-tight text-foreground">
            {title}
          </h3>

          <p className="text-sm md:text-[15px] leading-relaxed text-muted-foreground font-default">
            {description}
          </p>

          <div className="pt-4 mt-auto border-t border-border">
            <p className="font-serif italic text-xs md:text-sm text-muted-foreground/80">
              — {citation}
            </p>
          </div>
        </div>
      </Card>
    </div>;
}